import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

type TextVariant =
	| 'h1'
	| 'h2'
	| 'h3'
	| 'h4'
	| 'h5'
	| 'h6'
	| 'p'
	| 'span'
	| 'label'
	| 'small';
type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold';
type TextSize =
	| 'xs'
	| 'sm'
	| 'base'
	| 'lg'
	| 'xl'
	| '2xl'
	| '3xl'
	| '4xl'
	| '5xl';
type TextColor =
	| 'primary'
	| 'secondary'
	| 'muted'
	| 'danger'
	| 'success'
	| 'warning'
	| 'info';

interface TextProps extends React.HTMLAttributes<HTMLElement> {
	children: React.ReactNode;
	variant?: TextVariant;
	size?: TextSize;
	weight?: TextWeight;
	color?: TextColor;
	className?: string;
	as?: React.ElementType;
}

const variantElements: Record<TextVariant, React.ElementType> = {
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	h5: 'h5',
	h6: 'h6',
	p: 'p',
	span: 'span',
	label: 'label',
	small: 'small',
};

const sizeClasses: Record<TextSize, string> = {
	xs: 'text-xs',
	sm: 'text-sm',
	base: 'text-base',
	lg: 'text-lg',
	xl: 'text-xl',
	'2xl': 'text-2xl',
	'3xl': 'text-3xl',
	'4xl': 'text-4xl',
	'5xl': 'text-5xl',
};

const weightClasses: Record<TextWeight, string> = {
	normal: 'font-normal',
	medium: 'font-medium',
	semibold: 'font-semibold',
	bold: 'font-bold',
};

// Color classes will be determined dynamically in the component

// Default sizes for heading variants
const defaultSizes: Partial<Record<TextVariant, TextSize>> = {
	h1: '5xl',
	h2: '4xl',
	h3: '3xl',
	h4: '2xl',
	h5: 'xl',
	h6: 'lg',
	p: 'base',
	span: 'base',
	label: 'sm',
	small: 'xs',
};

// Default weights for heading variants
const defaultWeights: Partial<Record<TextVariant, TextWeight>> = {
	h1: 'bold',
	h2: 'bold',
	h3: 'semibold',
	h4: 'semibold',
	h5: 'medium',
	h6: 'medium',
	label: 'medium',
};

const TextComponent = React.forwardRef<HTMLElement, TextProps>(
	(
		{
			children,
			variant = 'span',
			size,
			weight,
			color = 'primary',
			className,
			as,
			...props
		},
		ref,
	) => {
		const colors = useThemeColors();
		const Component = as || variantElements[variant];
		const defaultSize = size || defaultSizes[variant] || 'base';
		const defaultWeight = weight || defaultWeights[variant] || 'normal';

		// Get color class based on the color prop
		const getColorClass = () => {
			switch (color) {
				case 'primary':
					return colors.classes.textPrimary;
				case 'secondary':
					return colors.classes.textSecondary;
				case 'muted':
					return colors.classes.textMuted;
				case 'danger':
					return colors.classes.textDanger;
				case 'success':
					return colors.classes.textSuccess;
				case 'warning':
					return colors.classes.textWarning;
				case 'info':
					return colors.classes.textInfo;
				default:
					return colors.classes.textPrimary;
			}
		};

		return (
			<Component
				ref={ref}
				className={cn(
					sizeClasses[defaultSize],
					weightClasses[defaultWeight],
					getColorClass(),
					className,
				)}
				{...props}
			>
				{(() => {
					// Add defensive check for children
					if (
						children &&
						typeof children === 'object' &&
						!React.isValidElement(children) &&
						!Array.isArray(children)
					) {
						// Error: Invalid children passed to Text component:
						return '[Invalid Content]';
					}
					return children;
				})()}
			</Component>
		);
	},
);

TextComponent.displayName = 'Text';

export const Text = TextComponent;
